import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { ScanRequestStatusValues, formatUTCDate } from "../../utils";
import messages from "./messages.i18n";
import cancelMessages from "../../upcoming-shoots/messages.i18n";
import { USER_ROLES } from "@constants/Roles";
import { PeekTypography } from "@piiqtechnologies/ui-components";
import { ScanRequestStatus } from "@piiqtechnologies/ui-lib";
import { useAuthContext } from "@stores/AuthContext";
import { Check } from "@piiqtechnologies/svg-icons";
import { sort } from "@utils/sort";
export const DefaultScanRequestStatus = [
    ScanRequestStatus.CANCELED,
    ScanRequestStatus.SCHEDULED,
    ScanRequestStatus.REQUESTED,
    ScanRequestStatus.SKIPPED,
];
export function ScanRequestsConfig({ communitySelected, isAdminTable, debouncedSearchKey, statusFilter, scanRequestsAPI, }) {
    var _a;
    const intl = useIntl();
    const { user } = useAuthContext();
    const scanRequestsParams = useMemo(() => {
        return {
            order: "desc",
            orderBy: "scheduledFor",
            search: debouncedSearchKey || undefined,
            communityId: communitySelected,
            status: statusFilter,
        };
    }, [statusFilter, debouncedSearchKey, communitySelected]);
    const showSpacesBuildingName = useMemo(() => {
        var _a, _b;
        return !!((_b = (_a = scanRequestsAPI === null || scanRequestsAPI === void 0 ? void 0 : scanRequestsAPI[1]) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.some((scanRequest) => { var _a, _b, _c, _d; return !!((_b = (_a = scanRequest.space) === null || _a === void 0 ? void 0 : _a.building) === null || _b === void 0 ? void 0 : _b.name) || !!((_d = (_c = scanRequest.space) === null || _c === void 0 ? void 0 : _c.building) === null || _d === void 0 ? void 0 : _d.alternativeName); }));
    }, [(_a = scanRequestsAPI === null || scanRequestsAPI === void 0 ? void 0 : scanRequestsAPI[1]) === null || _a === void 0 ? void 0 : _a.data]);
    const scanRequestsColumns = useMemo(() => {
        return [
            // REQUESTED ON
            {
                id: "createdAt",
                label: messages.requestedOn,
                sortable: true,
                render: (row) => formatUTCDate(row.createdAt),
            },
            // SPACE
            {
                id: "space.unit",
                label: messages.space,
                sortable: true,
                accessor: "space.unit",
            },
            // FLOOR PLAN
            ...((!isAdminTable && [
                {
                    id: "space.floorPlan.name",
                    label: messages.floorPlan,
                    sortable: true,
                    render: (row) => { var _a; return ((_a = row.space.floorPlan) === null || _a === void 0 ? void 0 : _a.name) || "-"; },
                },
            ]) ||
                []),
            // COMMUNITY
            ...((!isAdminTable && [
                {
                    id: "community",
                    label: messages.community,
                    sortable: false,
                    accessor: "community.name",
                },
            ]) ||
                []),
            // ORGANIZATION
            ...((!isAdminTable &&
                (user === null || user === void 0 ? void 0 : user.roleId) === USER_ROLES.Owner && [
                {
                    id: "community.organization.name",
                    label: messages.organization,
                    sortable: true,
                    accessor: "community.organization.name",
                },
            ]) ||
                []),
            // BUILDING NAME
            ...((showSpacesBuildingName &&
                isAdminTable && [
                {
                    id: "space.building.name",
                    label: messages.buildingName,
                    sortable: true,
                    render: (row) => { var _a, _b; return ((_a = row.space.building) === null || _a === void 0 ? void 0 : _a.name) || ((_b = row.space.building) === null || _b === void 0 ? void 0 : _b.alternativeName) || "-"; },
                },
            ]) ||
                []),
            // ADDRESS
            {
                id: "space.building.address.street",
                label: messages.address,
                sortable: isAdminTable,
                render: (row) => {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
                    return (React.createElement(PeekTypography, null, [
                        (_c = (_b = (_a = row.space) === null || _a === void 0 ? void 0 : _a.building) === null || _b === void 0 ? void 0 : _b.address) === null || _c === void 0 ? void 0 : _c.street,
                        (_f = (_e = (_d = row.space) === null || _d === void 0 ? void 0 : _d.building) === null || _e === void 0 ? void 0 : _e.address) === null || _f === void 0 ? void 0 : _f.city,
                        (_j = (_h = (_g = row.space) === null || _g === void 0 ? void 0 : _g.building) === null || _h === void 0 ? void 0 : _h.address) === null || _j === void 0 ? void 0 : _j.state,
                        (_m = (_l = (_k = row.space) === null || _k === void 0 ? void 0 : _k.building) === null || _l === void 0 ? void 0 : _l.address) === null || _m === void 0 ? void 0 : _m.postalCode,
                    ]
                        .filter(Boolean)
                        .join(", ") || "-"));
                },
            },
            ...((isAdminTable && [
                // AVAILABLE FROM
                {
                    id: "availableFrom",
                    label: messages.availableFrom,
                    sortable: true,
                    render: (row) => formatUTCDate(row.availableFrom),
                },
                // SCHEDULED FOR
                {
                    id: "scheduledFor",
                    label: messages.scheduledFor,
                    sortable: true,
                    render: (row) => (row.scheduledFor ? formatUTCDate(row.scheduledFor) : "-"),
                },
                // SCANNER EMAIL
                {
                    id: "scannerEmail",
                    label: messages.scannerEmail,
                    sortable: true,
                    accessor: "scannerEmail",
                    render: (row) => (row === null || row === void 0 ? void 0 : row.scannerEmail) || "-",
                },
                {
                    id: "status",
                    label: messages.status,
                    render: (row) => (React.createElement(PeekTypography, { color: ScanRequestStatusValues(row.status).color }, ScanRequestStatusValues(row.status).message)),
                    filterOptions: [
                        {
                            id: ScanRequestStatus.CANCELED,
                            label: intl.formatMessage(messages.scanRequestsStatusCanceledFilter),
                        },
                        {
                            id: ScanRequestStatus.REQUESTED,
                            label: intl.formatMessage(messages.scanRequestsStatusRequestedFilter),
                        },
                        {
                            id: ScanRequestStatus.SKIPPED,
                            label: intl.formatMessage(messages.scanRequestsStatusSkippedFilter),
                        },
                        {
                            id: ScanRequestStatus.SCHEDULED,
                            label: intl.formatMessage(messages.scanRequestsStatusScheduledFilter),
                        },
                    ],
                },
            ]) ||
                []),
            ...((!isAdminTable && [
                // AVAILABLE FROM
                {
                    id: "availableFrom",
                    label: messages.availableFrom,
                    sortable: false,
                    render: (row) => formatUTCDate(row.availableFrom),
                },
            ]) ||
                []),
            {
                id: "requestedBy.name",
                label: messages.requestedBy,
                sortable: isAdminTable,
                render: (row) => { var _a; return (row === null || row === void 0 ? void 0 : row.isAutoScan) ? intl.formatMessage(messages.autoScanLabel) : (_a = row === null || row === void 0 ? void 0 : row.requestedBy) === null || _a === void 0 ? void 0 : _a.name; },
            },
            {
                id: "isRescan",
                label: messages.rescan,
                sortable: false,
                render: (row) => (row === null || row === void 0 ? void 0 : row.isRescan) ? (React.createElement(PeekTypography, { variant: "h3" },
                    React.createElement(Check, null))) : ("-"),
            },
        ];
    }, [showSpacesBuildingName]);
    return {
        scanRequestsParams,
        scanRequestsColumns,
        sortPredicate: sort(),
    };
}
export function getDefaultCancellationReason(intl) {
    return intl.formatMessage(cancelMessages.reasonSpaceNoLongerAvailable);
}
export function buildInitialNotesAndReasons(selectedRows, existing, intl) {
    const next = {};
    selectedRows.forEach((r) => {
        const prev = (existing && existing[r._id]) || {};
        next[r._id] = {
            comment: prev.comment || "",
            reason: prev.reason || getDefaultCancellationReason(intl),
            subReason: prev.subReason || "",
        };
    });
    return next;
}
